'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useContext } from 'react';
import { v4 as uuid } from 'uuid';

import { postPositionApi } from '@flipster/apis/v2/trade';
import { LeverageBadge, OrderSideBadge } from '@flipster/features/src/misc/badge';
import { CurrencyDisplay } from '@flipster/features/src/misc/display';
import SegmentClick from '@flipster/features/src/misc/segment/segment-click';
import { orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { FormattedMessage } from '@flipster/i18n';
import { refDetailTimes } from '@flipster/r230522/original/server-time';
import { Button } from '@flipster/react/design-system/button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import Formatted from '@flipster/react/design-utils/formatted';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import ExternalLinkIcon from '@flipster/ui/assets/svgs/external-link.svg';
import { UsdtPrecision, getQuoteCurrencyPrecision, maxDecimalPrecision } from '@flipster/utils/constants/currency';
import { dateToEpochString } from '@flipster/utils/datetime';
import { getErrorMessage } from '@flipster/utils/error';
import { countDecimalPlaces } from '@flipster/utils/num';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import { useSymbolMarginType } from '@web/app/[locale]/(web)/trade/_misc/order-form/perpetual/bunja/base';

import { hedgingOrderLeverage, hedgingOrderSide } from '../constants';
import { HedgingFormContext, hedgingFormBunja } from './state';

interface PositionOrderButtonProps {
  onClose: () => void;
}

export const PositionOrderButton = ({ onClose }: PositionOrderButtonProps) => {
  const t = useTranslations();
  const router = useRouter();
  const { stakingCurrency } = useContext(HedgingFormContext);
  const { symbolInfoAtom, orderPriceAtom, pswapDetailAtom } = useBunja(hedgingFormBunja);
  const symbolInfo = useAtomValue(symbolInfoAtom);
  const orderPrice = useAtomValue(orderPriceAtom);
  const pswapDetail = useAtomValue(pswapDetailAtom);
  const attribution = useAtomValue(orderAttributionAtom);
  const marginType = useSymbolMarginType(symbolInfo?.symbol);

  if (!symbolInfo) return null;

  const { symbol, displayShortName } = symbolInfo;

  const submit = async () => {
    const requestId = uuid();
    const timestamp = dateToEpochString(new Date());
    const refTimestamp = refDetailTimes[symbol];
    if (!pswapDetail?.bidPrice) return;

    try {
      await postPositionApi(symbol, {
        side: hedgingOrderSide,
        requestId,
        timestamp,
        ...refTimestamp,
        qty: orderPrice,
        leverage: hedgingOrderLeverage,
        price: pswapDetail.bidPrice,
        attribution,
        marginType,
      });
      onClose();
      router.push(`/trade/${symbol}`);
      snackbar.success({
        message: <FormattedMessage id='Trade.r250401.Order_executed_toast' values={{ symbol: displayShortName }} />,
      });
    } catch (err) {
      snackbar.error({ message: getErrorMessage(err) });
    }
  };

  return (
    <SegmentClick event={StakeEvent.StakeHedgeOrderClick} data={{ currency: stakingCurrency }}>
      <Button
        as='button'
        htmlType='submit'
        onClick={submit}
        hierarchy='primary'
        color='sell'
        size='large'
        label={t('Autogen_common.r251126.s_Short_stakingCurrency', {
          stakingCurrency: stakingCurrency,
        })}
      />
    </SegmentClick>
  );
};

export const PositionOrderCard = () => {
  const { stakingCurrency } = useContext(HedgingFormContext);
  const {
    symbolInfoAtom,
    orderPriceAtom,
    marginAtom,
    pswapDetailAtom,
    estimatedTradingFeeRatePercentAtom,
    feeDiscountStateAtom,
    isFullDiscountAtom,
  } = useBunja(hedgingFormBunja);
  const symbolInfo = useAtomValue(symbolInfoAtom);
  const orderPrice = useAtomValue(orderPriceAtom);
  const margin = useAtomValue(marginAtom);
  const pswapDetail = useAtomValue(pswapDetailAtom);
  const estimatedTradingFeeRatePercent = useAtomValue(estimatedTradingFeeRatePercentAtom);
  const feeDiscountState = useAtomValue(feeDiscountStateAtom);
  const isFullDiscount = useAtomValue(isFullDiscountAtom);

  if (!symbolInfo) return null;

  const { quoteCurrency, unitOrderQty } = symbolInfo;

  return (
    <div className={css({ mt: 16, p: 12, bgColor: 'background.default.level-0', rounded: 8 })}>
      <div className={flex({ alignItems: 'center', gap: 8 })}>
        <CurrencyDisplay
          currency={stakingCurrency}
          icon={<CurrencyDisplay.Icon size={24} space={8} />}
          mainLabel={
            <CurrencyDisplay.Label
              value='shortName'
              css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-5' }}
            />
          }
        />
        <OrderSideBadge orderSide={hedgingOrderSide} />
        <LeverageBadge leverage={hedgingOrderLeverage} />
      </div>
      <dl className={css({ mt: 12, textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
        <div className={sectionDescriptionItem}>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Quantity' />
          </dt>
          <dd className={stack({ alignItems: 'flex-end' })}>
            <Formatted.Number
              value={orderPrice}
              suffix={stakingCurrency}
              precision={countDecimalPlaces(unitOrderQty)}
              toFixed
              css={sectionMainPrice}
            />
            <Formatted.Number
              value={margin}
              suffix={quoteCurrency}
              precision={getQuoteCurrencyPrecision(quoteCurrency)}
              prefix='≈ '
              css={{ textStyle: 'Text/M/Regular', color: 'content.default.level-2' }}
            />
          </dd>
        </div>
        <div className={sectionDescriptionItem}>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Margin' />
          </dt>
          <dd>
            <Formatted.Number
              value={margin}
              suffix={quoteCurrency}
              precision={getQuoteCurrencyPrecision(quoteCurrency)}
              toFixed
              css={sectionMainPrice}
            />
          </dd>
        </div>
        <div className={sectionDescriptionItem}>
          <dt className={hstack({ alignItems: 'center', gap: 4 })}>
            <IsomorphicLink
              href='/support/guide/fees'
              newTab
              className={hstack({ color: 'content.default.level-2', gap: 4 })}
            >
              Trading fee <ExternalLinkIcon />
            </IsomorphicLink>
          </dt>
          <dd className={stack({ alignItems: 'flex-end', gap: 4 })}>
            <div className={hstack({ gap: 4 })}>
              <Formatted.Percent
                value={estimatedTradingFeeRatePercent}
                precision={maxDecimalPrecision}
                css={{
                  textStyle: 'Text/L/Regular/Monospace',
                  color: isFullDiscount ? 'content.default.level-2' : 'content.default.level-5',
                  textDecoration: isFullDiscount ? 'line-through' : undefined,
                }}
              />
              <ArrowRightIcon size={14} color={token('colors.content.default.level-2')} />
              {isFullDiscount && (
                <Formatted.Percent value='0' precision={UsdtPrecision} toFixed css={sectionMainPrice} />
              )}
            </div>
            {feeDiscountState?.message != null && (
              <div className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                {feeDiscountState.message}
              </div>
            )}
          </dd>
        </div>
        <div className={sectionDescriptionItem}>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Est_price' />
          </dt>
          <dd className={stack({ alignItems: 'flex-end' })}>
            <Formatted.Number
              value={pswapDetail?.bidPrice}
              suffix={quoteCurrency}
              precision={getQuoteCurrencyPrecision(quoteCurrency)}
              css={sectionMainPrice}
            />
          </dd>
        </div>
      </dl>
    </div>
  );
};

const sectionDescriptionItem = flex({
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  '& + &': {
    mt: 12,
  },
});

const sectionMainPrice = {
  textStyle: 'Text/L/Regular/Monospace',
  color: 'content.default.level-5',
};
